import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/styles";
import {
  AppBar,
  Badge,
  Hidden,
  IconButton, ListItem, ListItemIcon, ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  colors,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box

} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import MenuIcon from "@material-ui/icons/Menu";
import { SwapHoriz, Star, StarBorder } from "@material-ui/icons";
import axios from "utils/axios";
import apiConfig, { baseUrl } from "apiConfig";
import localStorage from "utils/localStorage";
import Alert from "@material-ui/lab/Alert";
import { PERMISSIONS } from "common/permissions";
import jwt_decode from 'jwt-decode';
import Fab from "@material-ui/core/Fab";
import AddIcon from "@material-ui/icons/Add";
import CustomersModal from "./CustomersModal";
import AddressModal from "./AddressModal";
import List from '@material-ui/core/List';
import { checkFeature, FeatureFlags } from "FeatureService";
import { NotificationsNone, ExitToApp, Settings } from "@material-ui/icons";
import { SearchOutlined } from "@material-ui/icons";
import { useSelector } from "react-redux";
import SearchModal from "./SearchModal";
import { useMediaQuery } from '@material-ui/core';


const useStyles = makeStyles(theme => ({
  root: {
    boxShadow: "none"
  },
  flexGrow: {
    flexGrow: 1
  },
  signOutButton: {
    marginLeft: theme.spacing(1)
  },
  logoText: {
    color: "#fff",
    [theme.breakpoints.down('xs')]: {
      textAlign: 'center',
      fontSize: '1rem', // Reduced from 1.1rem
      lineHeight: 1.2, // Tighter line height
      fontWeight: 500,
      flex: 1,
      margin: '0 8px',
      wordBreak: 'break-word',
      hyphens: 'auto'
    }
    // width: "265px",
  },
  fab: {
    // marginRight: '10px',
    width: '38px',
    borderRadius: '20px',
    height: '30px',
    backgroundColor: colors.green[600],
    transition: 'inherit',
    flexShrink: 0, // Prevent buttons from shrinking
    [theme.breakpoints.down('xs')]: {
      width: '36px',
      height: '36px',
      minHeight: '36px'
    },
    '&:hover': {
      backgroundColor: colors.green[900]
    },
    '& > span.MuiFab-label': {
      transition: 'inherit'
    }

  },
  menuItem: {
    cursor: 'pointer',
    borderRadius: '4px',
    color: 'white',
    backgroundColor: colors.green[600],
    borderBottom: '1px solid white',
    paddingBottom: '4px',
    marginBottom: '4px',
    display: 'flex',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: colors.green[900],
    }
  },
  '.MuiTooltip-tooltipPlacementBottom': {
    paddingTop: '0px'
  },
  toolbar: {
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      flexDirection: 'column',
      alignItems: 'stretch',
      minHeight: 'auto',
      paddingTop: theme.spacing(1), // Reduced from 1.5
      paddingBottom: theme.spacing(1) // Reduced from 1.5
    },
    minHeight: 64,
    display: 'flex',
    alignItems: 'center'
  },
  mobileTopRow: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: theme.spacing(1), // Reduced from 1.5
      minHeight: 36 // Reduced from 40
    }
  },
  mobileBottomRow: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      gap: theme.spacing(1.5),
      flexWrap: 'wrap',
      paddingTop: 0 // Removed padding top
    }
  },
  quickAction: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    // marginLeft: 8,
    marginRight: 8,
    [theme.breakpoints.up('sm')]: {
      // width: 128,
      marginRight: 12,
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      position: 'relative' // Keep relative positioning for dropdown
    },
    transition: 'all 200ms ease 200ms',
    width: 38,
    minWidth: 38,
    '& $fab': {
      width: '100%'
    },
    '&:hover': {
      transitionDelay: 0,
      width: 128,
      minWidth: 128,
      '& $quickActionButtonTitle': {
        maxWidth: '100%'
      },
      '& $actionButtons': {
        visibility: 'visible',
        opacity: 1
      }
    }
  },
  alerts: {
    position: 'absolute',
    top: 64,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      top: 54
    }
  },
  quickActionButtonTitle: {
    maxWidth: 0,
    overflow: 'hidden',
    transition: 'inherit'
  },
  actionButtons: {
    position: 'absolute',
    top: 36,
    left: 0,
    right: 0,
    paddingTop: 4,
    visibility: 'hidden',
    opacity: 0,
    transition: 'inherit',
    zIndex: 1,
    [theme.breakpoints.down('xs')]: {
      top: 'calc(100% + 4px)', // Position below the button on mobile
      left: '50%', // Center horizontally
      transform: 'translateX(-50%)', // Perfect centering
      right: 'auto',
      width: 120,
      zIndex: 1300 // Higher z-index to appear above other elements
    }
  },
  topbarButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0, // Prevent the button container from shrinking
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.5),
      justifyContent: 'center',
      flexWrap: 'wrap'
    }
  },
  select: {
    backgroundColor: '#ffffff',
    fontSize: theme.spacing(2),
    '& div': {
      paddingTop: 10,
      paddingBottom: 10,
      minWidth: 90
    }
  },
  autocomplete: {
    backgroundColor: '#ffffff',
    minWidth: 200,
    maxWidth: 300,
    [theme.breakpoints.down('md')]: {
      minWidth: 150,
      maxWidth: 200
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 120,
      maxWidth: 150
    },
    '& .MuiInputBase-root': {
      fontSize: theme.spacing(2),
      paddingTop: 10,
      paddingBottom: 10,
      minWidth: 90,
      width: '100%'
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: '10px !important',
      paddingBottom: '10px !important',
      width: '100% !important'
    },
    '& .MuiOutlinedInput-root': {
      width: '100%'
    }
  },
  menuProps: {
    marginTop: 34,
    marginLeft: 1,
    minWidth: '134px !important'
  }

}));

const Topbar = props => {
  const { className, onSidebarOpen, ...rest } = props;
  const user = localStorage.getFullInfo();

  const cId = localStorage.getCompanyId();
  const { reloadTaskSideBar } = useSelector((state) => state.AppReducer);
  const classes = useStyles();
  const [listCompany, setListCompany] = useState(localStorage.getListCompany());
  const [company, setCompany] = useState(cId ? cId : user?.cid);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [openModalCustomers, setOpenModalCustomers] = React.useState(false);
  const [openModalAddress, setOpenModalAddress] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState();

  const [countsTask, setCountsTask] = useState(null);
  const [alerts, setAlerts] = useState(localStorage.getAlerts());

  const [isEnableTasks, setIsEnableTasks] = useState(false);
  checkFeature(FeatureFlags.TASKS).then(setIsEnableTasks);

  const roles = localStorage.getUserRoles();
  const hasTechnician = roles.includes(PERMISSIONS.FIELD_TECHNICIAN) || 
                       roles.includes(PERMISSIONS.FIELD_TECHNICIAN_ESTIMATOR) || 
                       roles.includes(PERMISSIONS.FIELD_TECHNICIAN_ESTIMATOR_SCHEDULE);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true
  });
  const companyName = useMemo(
    () => listCompany?.find(x => x.id === company)?.name || "Squadware One",
    [listCompany, company]
  );
  const [openSeach, setOpenSeach] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  // WI 4429 / #4442: this is the switcher that's actually rendered (ChangeLocation.js, where
  // the star was originally added, is never mounted anywhere) - self-service default-company
  // state duplicated here to match.
  const [settingDefault, setSettingDefault] = useState(false);
  const [justSetDefaultId, setJustSetDefaultId] = useState(null);

  // Determine if we should use searchable dropdown (5+ companies)
  const useSearchableDropdown = listCompany && listCompany.length >= 5;



  const fetchCountsTask = () => {
    if (!user) return;

    axios
      .get(apiConfig.url.BASE_URL + apiConfig.url.EMPLOYEE_COUNT_TASK, { params: { employeeId: user.nameid } })
      .then((res) => {
        setCountsTask(res.data);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') {
          console.log(err);
        }
      });
  };

  const clearAlert = index => {
    setAlerts(value => {
      value.splice(index, 1);
      localStorage.set("sw-alerts", JSON.stringify(value));
      return [...value];
    });
  };

  const updateCompany = async (companyId) => {
    setLoading(true);
    
    try {
      // Use the same approach as ChangeLocation component
      const response = await axios.post(
        apiConfig.url.AUTH_URL + apiConfig.url.COMPANY_CHANGE.replace('{companyId}', companyId)
      );
      
      const token = {
        token: response.data.token,
        expiresOn: response.data.expiresOn
      };
      const usr = {
        displayName: response.data.displayName,
        avatar: response.data.avatar
      };
      const info = jwt_decode(token.token);

      // WI #4451: the new company (or a group it belongs to) requires 2FA and this account
      // doesn't have it set up - the server already gates every real API call behind
      // 2FA-enrollment endpoints for a token carrying this claim (RequireTwoFactorSetupMiddleware),
      // so proceeding would just land the user on a dashboard where every call silently fails.
      // Refuse the switch here instead (rather than build a second, unverified copy of
      // SignIn.js's inline TwoFactorSetupRequired flow inside the topbar) - stay on the current
      // company and don't write any of the new token/role/company into localStorage.
      if (info.mustSetupTwoFactor === "true") {
        setLoading(false);
        setCompany(cId); // Reset to previous company
        // WI #4455: sign-in always resolves to the user's default company (there's no "choose a
        // company" step), so the earlier wording's suggestion to "sign back in choosing this
        // company" described a flow that doesn't exist. Point at an administrator instead of a
        // self-service step this page can't actually offer.
        alert('This company requires two-factor authentication, which is not yet set up on your account. Contact your administrator to complete setup.');
        return;
      }

      localStorage.set('companyId', info.cid);
      localStorage.set('sw-role', JSON.stringify(info.role));
      localStorage.set('sw-token', JSON.stringify(token));
      localStorage.set('sw-usr', JSON.stringify(usr));

      if (process.env.NODE_ENV === 'development') {
        console.log('Company switched successfully to:', companyId);
      }

      // Determine the default page based on user role
      const defaultPage = info.role.includes(PERMISSIONS.FIELD_TECHNICIAN)
        ? 'employees/today-route'
        : 'dashboard';

      // Navigate to the appropriate page
      window.location.href = baseUrl() + defaultPage;

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Company switch failed:', error);
      }
      
      setLoading(false);
      setCompany(cId); // Reset to previous company
      alert('Failed to switch company. Please try again or contact support.');
    }
  };

  const setAsDefaultCompany = companyId => {
    setSettingDefault(true);
    axios
      .post(apiConfig.url.AUTH_URL + apiConfig.url.SET_DEFAULT_COMPANY.replace('{companyId}', companyId))
      .then(() => setJustSetDefaultId(companyId))
      .catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to set default company', err);
        }
        // WI #4461: this used to fail silently, so the star just looked unresponsive.
        alert(err?.response?.data?.message || 'Could not set this as your default company. Please try again or contact support.');
      })
      .finally(() => setSettingDefault(false));
  };

  const handleLogout = async () => {
    try {
      await localStorage.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if server call fails
      localStorage.clearAll();
      window.location.href = '/sign-in';
    }
  };

  useEffect(() => {
    fetchCountsTask();
  }, [reloadTaskSideBar]);

  useEffect(() => {
    // Fetch company list if not already available
    if (!listCompany && user) {
      // Check if user has multi-company access
      const hasMultiCompanyAccess = roles.includes(PERMISSIONS.GROUP_ADMIN) || 
                                     roles.includes(PERMISSIONS.COMPANY_GROUP_ADMINISTRATOR) ||
                                     roles.includes(PERMISSIONS.SUPER_ADMIN);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Topbar - Fetching user companies:', {
          roles,
          userId: user?.nameid,
          userClaims: user
        });
      }
      
      // Fetch companies the user has access to
      // The backend filters this based on user's permissions
      axios
        .get(apiConfig.url.BASE_URL + apiConfig.url.COMPANY_ACTIVE_BASIC)
        .then(res => {
          const companies = res.data || [];
          if (process.env.NODE_ENV === 'development') {
            console.log('Topbar - Companies fetched:', companies);
          }
          setListCompany(companies);
          localStorage.set('listCompany', JSON.stringify(companies));
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching company list:', error);
          }
          // Fallback: fetch single company info
          axios
            .get(apiConfig.url.BASE_URL + apiConfig.url.COMPANY_BASIC.replace('{companyId}', user.cid))
            .then(res => {
              const singleCompany = [{
                id: res.data.id,
                name: res.data.name
              }];
              setListCompany(singleCompany);
              localStorage.set('listCompany', JSON.stringify(singleCompany));
            })
            .catch(() => {
              if (process.env.NODE_ENV === 'development') {
                console.error('Error fetching single company info');
              }
              // Final fallback
              const fallbackCompany = [{
                id: user.cid,
                name: 'Squadware One'
              }];
              setListCompany(fallbackCompany);
              localStorage.set('listCompany', JSON.stringify(fallbackCompany));
            });
        });
    }
  }, [listCompany, user, roles]);

  // Set selected company when listCompany is loaded
  useEffect(() => {
    if (listCompany && listCompany.length > 0) {
      const currentCompany = listCompany.find(comp => comp.id === company);
      setSelectedCompany(currentCompany || listCompany[0]);
    }
  }, [listCompany, company]);

  return (
    <AppBar {...rest} className={clsx(classes.root, className)} style={{ position: isDesktop ? 'fixed' : 'initial' }}>
      <Toolbar className={classes.toolbar}>
        {/* Mobile: Two-row layout */}
        <Hidden smUp>
          {/* Top row: Hamburger menu, Company name */}
          <div className={classes.mobileTopRow}>
            {onSidebarOpen && (
              <IconButton 
                color="inherit" 
                onClick={onSidebarOpen} 
                size="small"
                style={{ padding: 6 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <RouterLink 
              to={hasTechnician ? "/employees/today-route" : "/"} 
              style={{ 
                flex: 1, 
                textDecoration: 'none',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Tooltip
                arrow
                title={
                  <>
                    <Typography color="inherit">{hasTechnician ? "Today's Route" : "Dashboard"}</Typography>
                  </>
                }
              >
                <Typography variant="h4" className={classes.logoText}>
                  {companyName}
                </Typography>
              </Tooltip>
            </RouterLink>
          </div>
          
          {/* Bottom row: Action buttons */}
          <div className={classes.mobileBottomRow}>
            {!hasTechnician && (
              <Tooltip title={'Search'}>
                <Fab style={{ backgroundColor: '#43a047' }} className={classes.fab} color="secondary" onClick={() => setOpenSeach(true)}>
                  <Badge overlap="rectangular">
                    <SearchOutlined />
                  </Badge>
                </Fab>
              </Tooltip>
            )}
            
            {!hasTechnician && (
              <div className={classes.quickAction}>
                <Fab className={classes.fab} color="secondary">
                  <AddIcon className={classes.quickActionButtonIcon} />
                  <p className={classes.quickActionButtonTitle}>ACTIONS</p>
                </Fab>
                <List className={classes.actionButtons} component="nav" aria-label="main mailbox folders">
                  <ListItem onClick={() => setOpenModalCustomers(true)} className={classes.menuItem}>
                    <ListItemIcon style={{ color: '#ffffff', minWidth: 0 }}>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText style={{ flex: 'initial' }} primaryTypographyProps={{ style: { color: '#ffffff' } }} primary="JOB" />
                  </ListItem>
                  <ListItem className={classes.menuItem} button>
                    <Link to="/customers/add-customer" style={{ display: 'flex', justifyContent: 'center' }}>
                      <ListItemIcon style={{ color: '#ffffff', minWidth: 0 }}>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText style={{ flex: 'initial' }} primaryTypographyProps={{ style: { color: '#ffffff', display: 'inline' } }} primary="CUSTOMER" />
                    </Link>
                  </ListItem>

                  {isEnableTasks && (
                    <ListItem className={classes.menuItem} button>
                      <Link to="/employees/tasks?modal=true" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <ListItemIcon style={{ color: '#ffffff', minWidth: 0 }}>
                          <AddIcon />
                        </ListItemIcon>
                        <ListItemText style={{ flex: 'initial' }} primaryTypographyProps={{ style: { color: '#ffffff', display: 'inline' } }} primary="TASK" />
                      </Link>
                    </ListItem>
                  )}
                </List>
              </div>
            )}
            
            {listCompany && listCompany.length > 1 && (
              <Tooltip title="Switch Location">
                <Fab 
                  style={{ backgroundColor: '#43a047' }} 
                  className={classes.fab} 
                  color="secondary"
                  onClick={() => setOpenCompanyModal(true)}
                >
                  <SwapHoriz />
                </Fab>
              </Tooltip>
            )}
            
            <Link to="/employees/tasks">
              <Tooltip title={countsTask ? `Tasks Due Today (${countsTask})` : 'Tasks'}>
                <Fab style={{ backgroundColor: '#43a047' }} className={classes.fab} color="secondary">
                  <Badge badgeContent={countsTask} color="error" overlap="rectangular">
                    <NotificationsNone />
                  </Badge>
                </Fab>
              </Tooltip>
            </Link>

            <Link to={`/employees/${encodeURIComponent(user?.unique_name || user?.name || 'user')}/${user?.nameid}/security`}>
              <Tooltip title="User Settings">
                <Fab 
                  style={{ backgroundColor: '#43a047' }} 
                  className={classes.fab} 
                  color="secondary"
                >
                  <Settings />
                </Fab>
              </Tooltip>
            </Link>

            <Tooltip title="Logout">
              <Fab 
                style={{ backgroundColor: '#f44336' }} 
                className={classes.fab} 
                color="secondary"
                onClick={handleLogout}
              >
                <ExitToApp />
              </Fab>
            </Tooltip>
          </div>
        </Hidden>

        {/* Desktop: Single-row layout */}
        <Hidden xsDown>
          <RouterLink to={hasTechnician ? "/employees/today-route" : "/"}>
            <Tooltip
              arrow
              title={
                <>
                  <Typography color="inherit">{hasTechnician ? "Today's Route" : "Dashboard"}</Typography>
                </>
              }
            >
              <Typography variant="h4" className={classes.logoText}>
                {companyName}
              </Typography>
            </Tooltip>
          </RouterLink>
          <div className={classes.flexGrow} />
          {!hasTechnician && (
            <div>
              <Tooltip title={'Search'}>
                <Fab style={{ backgroundColor: '#43a047', marginRight: 12 }} className={classes.fab} color="secondary" onClick={() => setOpenSeach(true)}>
                  <Badge overlap="rectangular">
                    <SearchOutlined />
                  </Badge>
                </Fab>
              </Tooltip>
            </div>
          )}
          {!hasTechnician && (
            <div className={classes.quickAction}>
              <Fab className={classes.fab} color="secondary">
                <AddIcon className={classes.quickActionButtonIcon} />
                <p className={classes.quickActionButtonTitle}>ACTIONS</p>
              </Fab>
              <List className={classes.actionButtons} component="nav" aria-label="main mailbox folders">
                <ListItem onClick={() => setOpenModalCustomers(true)} className={classes.menuItem}>
                  <ListItemIcon style={{ color: '#ffffff', minWidth: 0 }}>
                    <AddIcon />
                  </ListItemIcon>
                  <ListItemText style={{ flex: 'initial' }} primaryTypographyProps={{ style: { color: '#ffffff' } }} primary="JOB" />
                </ListItem>
                <ListItem className={classes.menuItem} button>
                  <Link to="/customers/add-customer" style={{ display: 'flex', justifyContent: 'center' }}>
                    <ListItemIcon style={{ color: '#ffffff', minWidth: 0 }}>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText style={{ flex: 'initial' }} primaryTypographyProps={{ style: { color: '#ffffff', display: 'inline' } }} primary="CUSTOMER" />
                  </Link>
                </ListItem>

                {isEnableTasks && (
                  <ListItem className={classes.menuItem} button>
                    <Link to="/employees/tasks?modal=true" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <ListItemIcon style={{ color: '#ffffff', minWidth: 0 }}>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText style={{ flex: 'initial' }} primaryTypographyProps={{ style: { color: '#ffffff', display: 'inline' } }} primary="TASK" />
                    </Link>
                  </ListItem>
                )}
              </List>
            </div>
          )}
          <div className={classes.topbarButtons}>
            {listCompany && listCompany.length > 1 && (
              <Tooltip title="Switch Location">
                <Fab 
                  style={{ backgroundColor: '#43a047' }} 
                  className={classes.fab} 
                  color="secondary"
                  onClick={() => setOpenCompanyModal(true)}
                >
                  <SwapHoriz />
                </Fab>
              </Tooltip>
            )}
            
            <Link to="/employees/tasks">
              <Tooltip title={countsTask ? `Tasks Due Today (${countsTask})` : 'Tasks'}>
                <Fab style={{ backgroundColor: '#43a047' }} className={classes.fab} color="secondary">
                  <Badge badgeContent={countsTask} color="error" overlap="rectangular">
                    <NotificationsNone />
                  </Badge>
                </Fab>
              </Tooltip>
            </Link>

            <Link to={`/employees/${encodeURIComponent(user?.unique_name || user?.name || 'user')}/${user?.nameid}/security`}>
              <Tooltip title="User Settings">
                <Fab 
                  style={{ backgroundColor: '#43a047' }} 
                  className={classes.fab} 
                  color="secondary"
                >
                  <Settings />
                </Fab>
              </Tooltip>
            </Link>

            <Tooltip title="Logout">
              <Fab 
                style={{ backgroundColor: '#f44336' }} 
                className={classes.fab} 
                color="secondary"
                onClick={handleLogout}
              >
                <ExitToApp />
              </Fab>
            </Tooltip>
          </div>
          {onSidebarOpen && (
            <IconButton color="inherit" onClick={onSidebarOpen}>
              <MenuIcon />
            </IconButton>
          )}
        </Hidden>
      </Toolbar>

      {!hasTechnician && (
        <>
          <CustomersModal
            setSelectedCustomer={setSelectedCustomer}
            openModalAddress={setOpenModalAddress}
            open={openModalCustomers}
            onClose={() => setOpenModalCustomers(false)}
          >
          </CustomersModal>

          <AddressModal
            selectedCustomer={selectedCustomer}
            open={openModalAddress}
            onClose={() => {
              setOpenModalAddress(false);
              setOpenModalCustomers(false)
            }}
          >
          </AddressModal>
        </>
      )}

      <div className={classes.alerts}>
        {alerts.map((alert, index) =>
          <Alert elevation={6} variant="filled" severity={alert.severity} onClose={() => clearAlert(index)}>
            <Typography color="inherit" variant="h6">
              {alert.msg}
            </Typography>
          </Alert>
        )}
      </div>
      <SearchModal open={openSeach} onClose={() => setOpenSeach(false)} />
      
      {/* Company Switcher Modal */}
      <Dialog 
        open={openCompanyModal} 
        onClose={() => setOpenCompanyModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Switch Location</DialogTitle>
        <DialogContent>
          {useSearchableDropdown ? (
            <Autocomplete
              options={listCompany || []}
              getOptionLabel={(option) => option.name + (option.city ? ` | ${option.city}, ${option.state}` : '')}
              value={selectedCompany}
              onChange={(_event, newValue) => {
                if (newValue) {
                  setSelectedCompany(newValue);
                  setCompany(newValue.id);
                  // Don't close modal immediately, let updateCompany handle the refresh
                  updateCompany(newValue.id);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search locations..."
                  fullWidth
                  margin="normal"
                />
              )}
              renderOption={(option) => (
                <Typography>{option.name}{option.city ? ` | ${option.city}, ${option.state}` : ''}</Typography>
              )}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter(option =>
                  option.name.toLowerCase().includes(q) ||
                  (option.city && option.city.toLowerCase().includes(q)) ||
                  (option.state && option.state.toLowerCase().includes(q))
                );
              }}
              noOptionsText="No locations found"
              clearOnEscape
              disableClearable
            />
          ) : (
            <FormControl fullWidth margin="normal">
              <Select
                value={company}
                variant="outlined"
                onChange={(_e) => {
                  setCompany(_e.target.value);
                  // Don't close modal immediately, let updateCompany handle the refresh
                  updateCompany(_e.target.value);
                }}
              >
                {listCompany &&
                  listCompany.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}{item.city ? ` | ${item.city}, ${item.state}` : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
          {loading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
          )}
          {!loading && company && (
            <Box display="flex" alignItems="center" mt={1}>
              <Tooltip title="Set as my default company" arrow>
                <span>
                  <IconButton
                    aria-label="Set as my default company"
                    disabled={settingDefault}
                    onClick={() => setAsDefaultCompany(company)}
                  >
                    {justSetDefaultId === company
                      ? <Star style={{ color: '#f5b301' }} fontSize="small" />
                      : <StarBorder fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" color="textSecondary">
                Set as my default company
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompanyModal(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

Topbar.propTypes = {
  className: PropTypes.string,
  onSidebarOpen: PropTypes.func
};

export default Topbar;
